import type { CSSProperties, ReactNode, Ref, RefCallback } from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle, faCircleExclamation, faRotateRight, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '@course-authoring/shared-types';
import type { PublishJobProgress } from '@course-authoring/shared-types';
import type { PublishJobLanguageOutcome } from './publishJobSummaries';
import { PublishJobProgressBar } from './PublishJobProgressBar';

export type PublishJobPresentationState = 'queued' | 'running' | 'completed' | 'failed';
export type PublishJobPopoverPlacement = { left: number; width: number; maxHeight: number } & (
  | { top: number }
  | { bottom: number }
);

export function PublishJobLanguageList({ languages }: { languages: PublishJobLanguageOutcome[] }): React.ReactElement | null {
  const { tokens } = useTheme();
  if (languages.length === 0) return null;
  return <details style={{ marginTop: tokens.spacing.xs }}>
    <summary style={{ cursor: 'pointer', fontSize: 12 }}>Languages ({languages.length})</summary>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))', gap: tokens.spacing.xs, padding: `${tokens.spacing.xs} 0` }}>
      {languages.map((entry) => <span key={entry.locale} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0, padding: tokens.spacing.xs, fontSize: 12 }}>
        <span style={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacing.xs, minWidth: 0 }}>
          <FontAwesomeIcon icon={entry.failed ? faCircleExclamation : entry.done ? faCheck : faCircle} aria-hidden="true" style={{ color: entry.failed ? tokens.ui.errorText : entry.done ? tokens.ui.workflowApproved : tokens.text.muted, fontSize: 9, flexShrink: 0, marginTop: 3 }} />
          <span style={{ minWidth: 0, wordBreak: 'normal', overflowWrap: 'break-word', color: entry.done ? tokens.text.primary : tokens.text.muted }}>{entry.label}</span>
        </span>
        {entry.tags.length > 0 && <span style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.spacing.xs, paddingLeft: tokens.spacing.md }}>{entry.tags.map((tag) => <span key={tag} style={{ color: entry.failed ? tokens.ui.errorText : tokens.text.muted, whiteSpace: 'nowrap' }}>{tag}</span>)}</span>}
      </span>)}
    </div>
  </details>;
}

export function PublishJobStep({
  title, summary, progress, failed, done, running, languages, children,
}: {
  title: string;
  summary: string;
  progress: PublishJobProgress | null;
  failed: boolean;
  done: boolean;
  running: boolean;
  languages: PublishJobLanguageOutcome[];
  children?: ReactNode;
}): React.ReactElement {
  const { tokens } = useTheme();
  return <article style={{ padding: `${tokens.spacing.sm} 0`, borderBottom: `1px solid ${tokens.borders.default}` }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacing.sm }}>
      <span aria-label={failed ? 'Step failed' : done ? 'Step complete' : running ? 'Step in progress' : 'Step pending'} style={{ width: 22, height: 22, borderRadius: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: failed ? tokens.ui.errorText : done ? tokens.ui.workflowApproved : running ? tokens.ui.fixedAccent : tokens.text.muted, background: failed ? tokens.ui.errorBackground : done ? `color-mix(in srgb, ${tokens.ui.workflowApproved} 9%, ${tokens.backgrounds.page})` : running ? `color-mix(in srgb, ${tokens.ui.fixedAccent} 9%, ${tokens.backgrounds.page})` : tokens.backgrounds.page, flexShrink: 0 }}>
        <FontAwesomeIcon icon={failed ? faXmark : done ? faCheck : running ? faSpinner : faCircle} spin={running} aria-hidden="true" style={{ fontSize: running ? 12 : 9 }} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong>{title}</strong>
        <div style={{ fontSize: 13, color: tokens.text.muted }}>{summary}</div>
        {running && progress && <PublishJobProgressBar stepLabel={title} progress={progress} unit={languages.length ? 'languages' : 'items'} />}
        {children}
        <PublishJobLanguageList languages={languages} />
      </div>
    </div>
  </article>;
}

export function PublishJobStatusChip({
  state, label, accessibleLabel, progress, expanded, controls, onClick, onRetry, retrying = false, retryDisabled = false, retryLabel = 'Retry preparation', retryTitle = 'Retry preparation', retryDisabledReason = 'Retry is temporarily unavailable', triggerRef, trailing, draftState,
}: {
  state: PublishJobPresentationState;
  label: string;
  accessibleLabel: string;
  progress?: string | null;
  expanded: boolean;
  controls: string;
  onClick: () => void;
  onRetry?: () => void;
  retrying?: boolean;
  retryDisabled?: boolean;
  retryLabel?: string;
  retryTitle?: string;
  retryDisabledReason?: string;
  triggerRef?: Ref<HTMLButtonElement>;
  trailing?: ReactNode;
  draftState?: string | null;
}): React.ReactElement {
  const { tokens } = useTheme();
  const [hovered, setHovered] = useState(false);
  const failed = state === 'failed';
  const active = state === 'queued' || state === 'running';
  const completed = state === 'completed';
  const icon = failed ? faCircleExclamation : completed ? faCheck : faSpinner;
  const border = failed ? tokens.ui.errorBorder : active ? tokens.ui.focusRing : completed ? tokens.ui.workflowApproved : tokens.borders.default;
  const background = failed ? tokens.ui.errorBackground : active
    ? `color-mix(in srgb, ${tokens.ui.fixedAccent} 9%, ${tokens.backgrounds.page})`
    : completed ? `color-mix(in srgb, ${tokens.ui.workflowApproved} 9%, ${tokens.backgrounds.page})` : tokens.backgrounds.page;
  return <>
    <button ref={triggerRef} type="button" aria-label={accessibleLabel} aria-expanded={expanded} aria-controls={controls} onClick={onClick}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 24, padding: '0 9px', border: `1px solid ${border}`, borderRadius: 12, background, color: failed ? tokens.ui.errorText : tokens.text.primary, font: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, textDecoration: hovered ? 'underline' : 'none' }}>
      <FontAwesomeIcon icon={icon} spin={active} aria-hidden="true" />{label}{progress ? ` · ${progress}` : ''}{trailing}
    </button>
    {failed && onRetry && <button type="button" aria-label={retryLabel} title={retryDisabled ? retryDisabledReason : retryTitle} aria-busy={retrying} disabled={retrying || retryDisabled} onClick={onRetry}
      style={{ width: 24, height: 24, border: `1px solid ${tokens.ui.errorBorder}`, borderRadius: tokens.radius.sm, background: tokens.backgrounds.page, color: tokens.ui.errorText, cursor: retrying || retryDisabled ? 'not-allowed' : 'pointer', opacity: retrying || retryDisabled ? 0.6 : 1, padding: 0, flexShrink: 0 }}>
      <FontAwesomeIcon icon={faRotateRight} spin={retrying} aria-hidden="true" style={{ fontSize: 11 }} />
    </button>}
    {draftState && <span style={{ color: tokens.text.muted, fontSize: 12, whiteSpace: 'nowrap' }}>{draftState}</span>}
  </>;
}

export function PublishJobDetailsPanel({
  title, subtitle, placement, panelRef, onClose, popoverId, busy = false, children,
}: {
  title: string;
  subtitle: string;
  placement: PublishJobPopoverPlacement;
  panelRef: RefCallback<HTMLElement>;
  onClose: () => void;
  popoverId: string;
  busy?: boolean;
  children: ReactNode;
}): React.ReactElement {
  const { tokens } = useTheme();
  const titleId = `${popoverId}-title`;
  const panelStyle: CSSProperties = {
    position: 'fixed', zIndex: 1200, left: placement.left, width: placement.width, maxWidth: placement.width,
    maxHeight: placement.maxHeight, boxSizing: 'border-box', overflowY: 'auto', display: 'flex', flexDirection: 'column',
    border: `1px solid ${tokens.borders.default}`, borderRadius: tokens.radius.md, background: tokens.backgrounds.page,
    color: tokens.text.primary, boxShadow: '0 8px 24px rgba(23,25,50,0.16)', fontFamily: tokens.typography.fontFamily,
    fontSize: 13, ...('top' in placement ? { top: placement.top } : { bottom: placement.bottom }),
  };
  return createPortal(<section ref={panelRef} id={popoverId} role="dialog" tabIndex={-1} aria-labelledby={titleId} aria-busy={busy} style={panelStyle}>
    <header style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing.sm, padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderBottom: `1px solid ${tokens.borders.default}`, flexShrink: 0 }}>
      <div style={{ flex: 1, minWidth: 0 }}><strong id={titleId}>{title}</strong><div style={{ color: tokens.text.muted, fontSize: 12 }}>{subtitle}</div></div>
      <button type="button" aria-label="Close preparation details" onClick={onClose} style={{ marginLeft: 'auto', width: 26, height: 26, border: 0, borderRadius: tokens.radius.sm, background: 'transparent', color: tokens.text.muted, cursor: 'pointer', flexShrink: 0 }}>
        <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
      </button>
    </header>
    {children}
  </section>, document.body);
}
